import React, { useState, useEffect } from 'react';
import { Controller, Control, UseFormWatch, UseFormSetValue, FieldErrors } from 'react-hook-form';
import { CollaboratorFormContext } from "@/features/Collaborators/Edit/Contexts/CollaboratorFormContext";
import { collaboratorSchemaHelpers } from '@/features/Collaborators/Models/collaborator';
import { IssuingBodyCombobox, StateCombobox, FileUploadInput, RequiredIndicator } from '@/components/shared';
import { CollaboratorFormData } from '@/features/Collaborators/Edit/Types';
import { useFileUpload } from '@/hooks/form-handlers/useFileUpload';

interface IdentificationFieldsetProps {
    context: CollaboratorFormContext;
    collaboratorId: string;
    control: Control<CollaboratorFormData>;
    setValue: UseFormSetValue<CollaboratorFormData>;
    watch: UseFormWatch<CollaboratorFormData>;
    errors?: FieldErrors<CollaboratorFormData>;
    isSubmitting?: boolean;
    layout?: 'full' | 'compact';
}

const IdentificationFieldset: React.FC<IdentificationFieldsetProps> = ({
    context,
    collaboratorId,
    control,
    setValue,
    watch,
    errors = {},
    isSubmitting = false,
    layout = 'full'
}) => {
    // Helper local para extrair mensagem de erro simples
    const getErrorMessage = (fieldName: string): string | undefined => {
        const error = (errors as any)?.[fieldName];
        if (error && typeof error === 'object' && 'message' in error) {
            return error.message as string;
        }
        return undefined;
    };

    // Helper para verificar se campo deve aparecer usando schema unificado
    const shouldShow = (fieldName: string) => collaboratorSchemaHelpers.shouldShowField(fieldName, context);

    // Helper para verificar se campos são obrigatórios usando schema unificado
    const isRequired = (fieldName: string) => collaboratorSchemaHelpers.isFieldRequired(fieldName, context);

    // Helper específico para campos aninhados do identityDocument
    const isIdentityDocumentFieldRequired = (fieldName: string) => {
        // Para campos aninhados, verificamos se o contexto atual exige validação
        if (context === 'PROFILE_UPDATE' as CollaboratorFormContext) {
            return ['number', 'issuer', 'uf', 'issueDate'].includes(fieldName);
        }
        return false;
    };

    // Helper específico para mensagens de erro de campos aninhados
    const getNestedErrorMessage = (fieldPath: string) => {
        const pathParts = fieldPath.split('.');
        let errorObj: any = errors;

        for (const part of pathParts) {
            if (errorObj && typeof errorObj === 'object' && part in errorObj) {
                errorObj = errorObj[part];
            } else {
                return undefined;
            }
        }

        if (errorObj && typeof errorObj === 'object' && 'message' in errorObj) {
            return errorObj.message;
        }
        return undefined;
    };

    // Obter valores específicos do React Hook Form
    const watchedIdDocumentFrontUrl = watch('identityDocument.idDocumentFrontUrl');
    const watchedIdDocumentBackUrl = watch('identityDocument.idDocumentBackUrl');
    const watchedIdDocumentFrontFile = watch('idDocumentFrontFile');
    const watchedIdDocumentBackFile = watch('idDocumentBackFile');

    // Estado local para os dados do formulário (necessário para o useFileUpload)
    const [formData, setFormData] = useState<CollaboratorFormData>({} as CollaboratorFormData);

    // File upload hook
    const { uploadStates, handleFileChange } = useFileUpload(setFormData);

    // Sincronizar mudanças do React Hook Form com o estado local
    useEffect(() => {
        const subscription = watch((value) => {
            setFormData(value as CollaboratorFormData);
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // URLs para os campos de upload (prioriza dados do formulário)
    const idDocumentFrontUrl = watchedIdDocumentFrontUrl || formData.identityDocument?.idDocumentFrontUrl;
    const idDocumentBackUrl = watchedIdDocumentBackUrl || formData.identityDocument?.idDocumentBackUrl;

    // useEffect para atualizar URLs quando mudarem
    useEffect(() => {
        if (idDocumentFrontUrl) {
            setValue('identityDocument.idDocumentFrontUrl', idDocumentFrontUrl);
        }
    }, [idDocumentFrontUrl, setValue]);

    useEffect(() => {
        if (idDocumentBackUrl) {
            setValue('identityDocument.idDocumentBackUrl', idDocumentBackUrl);
        }
    }, [idDocumentBackUrl, setValue]);

    // Estados de upload específicos para documentos de identificação
    const idDocumentFrontState = uploadStates['idDocumentFrontFile'];
    const idDocumentBackState = uploadStates['idDocumentBackFile'];

    return (
        <fieldset className="border p-4 rounded-md">
            <legend className="text-sm font-medium px-1 text-gray-400">Identificação</legend>
            <div className="space-y-4 pt-2">
                <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2'}`}>
                    {/* CPF */}
                    {shouldShow('cpf') && (
                        <div>
                            <label htmlFor="cpf" className="block text-sm font-medium text-gray-700">
                                CPF
                                {isRequired('cpf') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="cpf"
                                control={control}
                                render={({ field: { value, ...field } }) => (
                                    <input
                                        {...field}
                                        value={value || ''}
                                        id="cpf"
                                        className="input-primary w-full mt-1"
                                        placeholder="000.000.000-00"
                                        type="tel"
                                        disabled={isSubmitting}
                                        maxLength={14}
                                        onChange={(e) => {
                                            // Aplicar máscara do CPF
                                            let value = e.target.value.replace(/\D/g, ''); // Remove tudo que não é dígito

                                            if (value.length <= 11) {
                                                // Aplicar máscara: 000.000.000-00
                                                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                                                value = value.replace(/(\d{3})(\d)/, '$1.$2');
                                                value = value.replace(/(\d{3})(\d{1,2})$/, '$1-$2');
                                            }

                                            // Atualizar o campo
                                            field.onChange(value);
                                        }}
                                    />
                                )}
                            />
                            {getErrorMessage('cpf') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getErrorMessage('cpf')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* e-Social Code */}
                    {shouldShow('esocialCode') && (
                        <div>
                            <label htmlFor="esocialCode" className="block text-sm font-medium text-gray-700">
                                Código do e-Social
                                {isRequired('esocialCode') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="esocialCode"
                                control={control}
                                render={({ field: { value, ...field } }) => (
                                    <input
                                        {...field}
                                        value={value || ''}
                                        id="esocialCode"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {getErrorMessage('esocialCode') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getErrorMessage('esocialCode')}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Documento de Identidade */}
                <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
                    {/* Número do Documento */}
                    {shouldShow('identityDocument') && (
                        <div>
                            <label htmlFor="id-number" className="block text-sm font-medium text-gray-700">
                                Nº do Documento de Identificação
                                {isIdentityDocumentFieldRequired('number') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="identityDocument.number"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        id="id-number"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {getNestedErrorMessage('identityDocument.number') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getNestedErrorMessage('identityDocument.number')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Órgão Emissor */}
                    {shouldShow('identityDocument') && (
                        <div>
                            <label htmlFor="id-issuer" className="block text-sm font-medium text-gray-700">
                                Órgão Emissor
                                {isIdentityDocumentFieldRequired('issuer') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="identityDocument.issuer"
                                control={control}
                                render={({ field }) => (
                                    <IssuingBodyCombobox
                                        selectedValue={field.value}
                                        onChange={(value) => field.onChange(value)}
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {getNestedErrorMessage('identityDocument.issuer') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getNestedErrorMessage('identityDocument.issuer')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* UF */}
                    {shouldShow('identityDocument') && (
                        <div>
                            <label htmlFor="id-uf" className="block text-sm font-medium text-gray-700">
                                UF
                                {isIdentityDocumentFieldRequired('uf') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="identityDocument.uf"
                                control={control}
                                render={({ field }) => (
                                    <StateCombobox
                                        selectedValue={field.value}
                                        onChange={(value) => field.onChange(value)}
                                        disabled={isSubmitting}
                                    />
                                )}
                            />
                            {getNestedErrorMessage('identityDocument.uf') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getNestedErrorMessage('identityDocument.uf')}
                                </p>
                            )}
                        </div>
                    )}
                </div>

                {/* Data de Expedição e Upload de Arquivos */}
                <div className={`grid gap-4 ${layout === 'compact' ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-3'}`}>
                    {/* Data de Expedição */}
                    {shouldShow('identityDocument') && (
                        <div>
                            <label htmlFor="issue-date" className="block text-sm font-medium text-gray-700">
                                Data de Expedição
                                {isIdentityDocumentFieldRequired('issueDate') && <RequiredIndicator />}
                            </label>
                            <Controller
                                name="identityDocument.issueDate"
                                control={control}
                                render={({ field }) => (
                                    <input
                                        {...field}
                                        value={field.value || ''}
                                        type="date"
                                        id="issue-date"
                                        className="input-primary w-full mt-1"
                                        disabled={isSubmitting}
                                        onChange={(e) => {
                                            field.onChange(e.target.value);
                                        }}
                                    />
                                )}
                            />
                            {getNestedErrorMessage('identityDocument.issueDate') && (
                                <p className="mt-1 text-sm text-red-600">
                                    {getNestedErrorMessage('identityDocument.issueDate')}
                                </p>
                            )}
                        </div>
                    )}

                    {/* Upload Documento Frente */}
                    {shouldShow('idDocumentFrontFile') && (
                        <div>
                            <Controller
                                name="idDocumentFrontFile"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FileUploadInput
                                        name="idDocumentFrontFile"
                                        label="Doc. Identificação (Frente ou Frente e Verso)"
                                        fileUrl={idDocumentFrontUrl}
                                        fileName={watchedIdDocumentFrontFile?.name}
                                        isSubmitting={isSubmitting}
                                        progress={idDocumentFrontState?.progress}
                                        status={idDocumentFrontState?.status}
                                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;

                                            field.onChange(file);
                                            await handleFileChange(e, {
                                                fieldName: 'idDocumentFrontFile',
                                                urlFieldName: 'identityDocument.idDocumentFrontUrl',
                                                pathPrefix: 'identification',
                                                id: collaboratorId
                                            });
                                        }}
                                        isRequired={context === 'PROFILE_UPDATE'}
                                        error={fieldState.error?.message || getErrorMessage('idDocumentFrontFile')}
                                    />
                                )}
                            />
                        </div>
                    )}

                    {/* Upload Documento Verso */}
                    {shouldShow('idDocumentBackFile') && (
                        <div>
                            <Controller
                                name="idDocumentBackFile"
                                control={control}
                                render={({ field, fieldState }) => (
                                    <FileUploadInput
                                        name="idDocumentBackFile"
                                        label="Documento de Identificação (Verso)"
                                        fileUrl={idDocumentBackUrl}
                                        fileName={watchedIdDocumentBackFile?.name}
                                        isSubmitting={isSubmitting}
                                        progress={idDocumentBackState?.progress}
                                        status={idDocumentBackState?.status}
                                        onChange={async (e: React.ChangeEvent<HTMLInputElement>) => {
                                            const file = e.target.files?.[0];
                                            if (!file) return;

                                            field.onChange(file);
                                            await handleFileChange(e, {
                                                fieldName: 'idDocumentBackFile',
                                                urlFieldName: 'identityDocument.idDocumentBackUrl',
                                                pathPrefix: 'identification',
                                                id: collaboratorId
                                            });
                                        }}
                                        isRequired={false} // ✅ Verso nunca é obrigatório
                                        error={fieldState.error?.message || getErrorMessage('idDocumentBackFile')}
                                    />
                                )}
                            />
                        </div>
                    )}
                </div>
            </div>
        </fieldset>
    );
};

export default IdentificationFieldset;
